import { useEffect, useRef, useState } from "react";
import { Headset, MessageCircle, X } from "lucide-react";
import { useLocation } from "wouter";
import { openWhatsApp } from "@/components/WhatsAppLeadModal";

/** Ícone oficial do WhatsApp (o lucide não possui a marca). */
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="currentColor"
      className={className}
      aria-hidden="true">
      <path d="M16.004 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.26.59 4.46 1.71 6.4L3.2 28.8l6.58-1.67a12.74 12.74 0 0 0 6.22 1.6h.01c7.06 0 12.79-5.74 12.79-12.8s-5.74-12.73-12.8-12.73zm0 23.39h-.01a10.6 10.6 0 0 1-5.41-1.48l-.39-.23-4.02 1.02 1.07-3.86-.25-.4a10.55 10.55 0 0 1-1.63-5.64c0-5.86 4.78-10.63 10.65-10.63 2.84 0 5.51 1.11 7.52 3.11a10.56 10.56 0 0 1 3.11 7.53c0 5.86-4.78 10.58-10.64 10.58zm5.83-7.97c-.32-.16-1.89-.93-2.18-1.04-.29-.11-.5-.16-.72.16-.21.32-.82 1.04-1.01 1.25-.18.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.59-.95-.85-1.59-1.9-1.78-2.22-.19-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.19.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.72-1.73-.98-2.37-.26-.62-.52-.54-.72-.55l-.61-.01c-.21 0-.56.08-.85.4-.29.32-1.12 1.09-1.12 2.66 0 1.57 1.14 3.09 1.3 3.3.16.21 2.25 3.44 5.45 4.82.76.33 1.36.53 1.82.67.77.24 1.46.21 2.01.13.61-.09 1.89-.77 2.16-1.52.27-.75.27-1.39.19-1.52-.08-.13-.29-.21-.61-.37z" />
    </svg>
  );
}

/**
 * Botão flutuante de WhatsApp com menu duplo — canto inferior direito.
 * Ao clicar, expande um menu com duas opções: Comercial e Suporte Técnico.
 * Exibe um balão de boas-vindas após 4 segundos na página.
 */
export default function WhatsAppFloat() {
  const [location] = useLocation();
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [showBalloon, setShowBalloon] = useState(false);
  const [balloonDismissed, setBalloonDismissed] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const balloonTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mostrar botão após scroll
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 200);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Mostrar balão após 4 segundos (apenas uma vez por sessão)
  useEffect(() => {
    if (!visible || balloonDismissed) return;

    balloonTimerRef.current = setTimeout(() => {
      setShowBalloon(true);
      // Auto-dispensar após 7 segundos
      dismissTimerRef.current = setTimeout(() => {
        setShowBalloon(false);
        setBalloonDismissed(true);
      }, 7000);
    }, 4000);

    return () => {
      if (balloonTimerRef.current) clearTimeout(balloonTimerRef.current);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, [visible, balloonDismissed]);

  // Fechar balão quando o menu abre
  useEffect(() => {
    if (open) {
      setShowBalloon(false);
      setBalloonDismissed(true);
      if (balloonTimerRef.current) clearTimeout(balloonTimerRef.current);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    }
  }, [open]);

  // Fechar menu ao clicar fora
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const dismissBalloon = () => {
    setShowBalloon(false);
    setBalloonDismissed(true);
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
  };

  if (!visible) return null;

  return (
    <div
      ref={menuRef}
      className={`fixed right-5 z-50 flex flex-col items-end gap-2 ${location.startsWith("/produtos/") ? "bottom-24 sm:bottom-5" : "bottom-5"}`}>

      {/* ── Balão de boas-vindas ── */}
      <div
        aria-live="polite"
        className={`relative mb-1 mr-1 flex max-w-[220px] flex-col gap-1 rounded-2xl rounded-br-sm bg-white px-4 py-3 shadow-[0_4px_20px_rgba(0,0,0,0.15)] transition-all duration-500 ease-out ${
          showBalloon
            ? "translate-y-0 scale-100 opacity-100 pointer-events-auto"
            : "translate-y-4 scale-95 opacity-0 pointer-events-none"
        }`}>
        {/* Triângulo apontando para o botão */}
        <span
          aria-hidden="true"
          className="absolute -bottom-2 right-4 h-0 w-0 border-l-[8px] border-r-[8px] border-t-[8px] border-l-transparent border-r-transparent border-t-white"
        />
        {/* Botão fechar */}
        <button
          onClick={dismissBalloon}
          aria-label="Fechar mensagem"
          className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-charcoal/20 text-charcoal/60 transition-colors hover:bg-charcoal/40 hover:text-charcoal">
          <X className="h-3 w-3" />
        </button>
        <p className="text-xs font-semibold text-charcoal">
          Olá! 👋 Podemos ajudar?
        </p>
        <p className="text-xs leading-snug text-charcoal/65">
          Tire suas dúvidas com nossa equipe agora mesmo.
        </p>
      </div>

      {/* Menu de opções — aparece ao clicar */}
      <div
        className={`flex flex-col items-end gap-2 transition-all duration-300 ease-out ${
          open
            ? "translate-y-0 opacity-100 pointer-events-auto"
            : "translate-y-3 opacity-0 pointer-events-none"
        }`}
        aria-hidden={!open}>
        {/* Opção: Suporte Técnico */}
        <button
          type="button"
          onClick={() => { setOpen(false); openWhatsApp({ message: 'Olá! Preciso de assistência técnica para um equipamento EquipaPRO.', channel: 'suporte', origin: 'float-suporte' }); }}
          tabIndex={open ? 0 : -1}
          className="group flex items-center gap-3 rounded-full bg-blue-600 pl-4 pr-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-900/30 transition-all duration-200 hover:scale-[1.03] hover:bg-blue-500 hover:shadow-blue-600/40 active:scale-95">
          <Headset className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:-translate-y-0.5" />
          Suporte Técnico
        </button>
        {/* Opção: Atendimento Comercial */}
        <button
          type="button"
          onClick={() => { setOpen(false); openWhatsApp({ message: 'Olá! Gostaria de falar com o atendimento comercial da EquipaPRO.', channel: 'comercial', origin: 'float-comercial' }); }}
          tabIndex={open ? 0 : -1}
          className="group flex items-center gap-3 rounded-full bg-[#25D366] pl-4 pr-5 py-3 text-sm font-semibold text-white shadow-lg shadow-green-900/30 transition-all duration-200 hover:scale-[1.03] hover:bg-[#20c05e] hover:shadow-green-600/40 active:scale-95">
          <MessageCircle className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:-translate-y-0.5" />
          Atendimento Comercial
        </button>
      </div>

      {/* Pulse ring — apenas quando o menu está fechado */}
      {!open && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 right-0 h-14 w-14 rounded-full bg-[#25D366] opacity-0 animate-[whatsapp-pulse_2.4s_ease-out_infinite]"
        />
      )}

      {/* Botão principal */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Fechar menu de WhatsApp" : "Falar com a EquipaPRO no WhatsApp"}
        aria-expanded={open}
        className={`flex h-14 w-14 items-center justify-center rounded-full shadow-[0_4px_16px_rgba(0,0,0,0.25)] transition-all duration-300 ease-out hover:scale-105 hover:shadow-[0_6px_20px_rgba(0,0,0,0.3)] active:scale-95 ${
          open
            ? "bg-gray-700 text-white rotate-0"
            : "bg-[#25D366] text-white"
        }`}>
        {open ? (
          <X className="h-6 w-6 transition-transform duration-200" />
        ) : (
          <WhatsAppIcon className="h-7 w-7" />
        )}
      </button>
    </div>
  );
}
